import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitInterestedBuyersWidgets } from '~/config/widgets/cockpit/cockpitInterestedBuyersWidgets';
import { useAuthz } from '~/context/AuthzContext';
import {
  Bars,
  fmtDate,
  Kpi,
  PageHead,
  Privacy,
  Steps,
  V3Page,
} from '~/components/cockpit/v3';
import type { WidgetProps } from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
)({
  component: InterestPage,
});

interface Buyer {
  readonly id?: string;
  readonly firstname?: string;
  readonly lastname?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly mobile?: string;
  readonly status?: string;
  readonly hasDownloaded?: boolean;
  readonly hasOpened?: boolean;
  readonly hasBookedVisit?: boolean;
  readonly hasVisited?: boolean;
  readonly hasGivenOffer?: boolean;
  readonly reason?: string;
  readonly createdAt?: string;
}

// The loader always sends staff objects; an unresolved one has no name fields.
// HubDB records may carry only `name`.
function staffName(staff?: {
  firstname?: string;
  lastname?: string;
  name?: string;
}): string {
  return (
    [
      staff?.firstname,
      staff?.lastname,
    ]
      .filter(Boolean)
      .join(' ') ||
    staff?.name ||
    ''
  );
}

function InterestPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitInterestedBuyersWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const buyers: Buyer[] = wp.cockpitInterestedBuyersDetailWidget?.data ?? [];
  const reasons: { reason: string; count: number }[] =
    wp.cockpitRejectionReasonsGraphWidget?.data ?? [];
  const [
    total = buyers.length,
    lost = 0,
  ] = wp.cockpitInterestedBuyers?.numbers ?? [];
  const agent = wp.cockpitInternalExternalAgentWidget ?? {};
  const internalName = staffName(agent.internalAgent);
  const externalName = staffName(agent.externalAgent);

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_interest_title', 'Interessenten')}
        sub={t(
          'cockpit_interest_sub',
          'Wer sich für Ihre Immobilie interessiert und wie weit der Prozess ist.',
        )}
      />
      <div
        className="grid g3"
        data-widget="cockpitInterestedBuyers"
      >
        <Kpi
          value={total}
          delta={wp.cockpitInterestedBuyers?.changeInLastWeek}
          label={t('cockpit_interest_kpi_total', 'Interessenten')}
        />
        <Kpi
          value={buyers.filter((b) => b.hasBookedVisit).length}
          label={t('cockpit_interest_kpi_visit', 'Mit Besichtigung')}
        />
        <Kpi
          value={lost}
          label={t('cockpit_interest_kpi_lost', 'Abgesprungen')}
        />
      </div>

      <div className="cols">
        <section
          className="card"
          data-widget="cockpitInterestedBuyersDetailWidget"
        >
          <div className="sec">
            <h2>{t('cockpit_interest_list', 'Alle Interessenten')}</h2>
          </div>
          {buyers.length === 0 ? (
            <p className="mu">
              {t('cockpit_interest_empty', 'Noch keine Interessenten.')}
            </p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('cockpit_interest_col_name', 'Name')}</th>
                  <th>{t('cockpit_interest_col_date', 'Seit')}</th>
                  <th>{t('cockpit_interest_col_progress', 'Fortschritt')}</th>
                  <th>{t('cockpit_interest_col_status', 'Status')}</th>
                  {isAdvisor ? (
                    <th>{t('cockpit_interest_col_contact', 'Kontakt')}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {buyers.map((b, i) => {
                  const isLost = b.status === 'notinterested';
                  const phone = b.mobile || b.phone;
                  return (
                    <tr key={b.id ?? i}>
                      <td data-l={t('cockpit_interest_col_name', 'Name')}>
                        {[
                          b.firstname,
                          b.lastname,
                        ]
                          .filter(Boolean)
                          .join(' ') || '–'}
                      </td>
                      <td data-l={t('cockpit_interest_col_date', 'Seit')}>
                        {fmtDate(b.createdAt)}
                      </td>
                      <td
                        data-l={t(
                          'cockpit_interest_col_progress',
                          'Fortschritt',
                        )}
                      >
                        <Steps
                          done={[
                            !!(b.hasOpened || b.hasDownloaded),
                            !!b.hasBookedVisit,
                            !!b.hasVisited,
                            !!b.hasGivenOffer,
                          ]}
                          label={
                            isLost
                              ? t('cockpit_interest_lost', 'Abgesprungen')
                              : b.hasGivenOffer
                              ? t('cockpit_interest_step_offer', 'Angebot')
                              : b.hasVisited
                              ? t('cockpit_interest_step_visited', 'Besichtigt')
                              : b.hasBookedVisit
                              ? t(
                                  'cockpit_interest_step_booked',
                                  'Termin gebucht',
                                )
                              : t('cockpit_interest_step_open', 'Interesse')
                          }
                        />
                      </td>
                      <td data-l={t('cockpit_interest_col_status', 'Status')}>
                        <span className={`chip ${isLost ? 'c-bad' : 'c-ok'}`}>
                          {isLost
                            ? b.reason ??
                              t('cockpit_interest_lost', 'Abgesprungen')
                            : t('cockpit_interest_active', 'Aktiv')}
                        </span>
                      </td>
                      {isAdvisor ? (
                        <td
                          data-l={t('cockpit_interest_col_contact', 'Kontakt')}
                        >
                          <span>
                            {b.email ? (
                              <a
                                className="lnk"
                                href={`mailto:${b.email}`}
                              >
                                {b.email}
                              </a>
                            ) : null}
                            {phone ? (
                              <a
                                className="lnk"
                                href={`tel:${phone}`}
                              >
                                {phone}
                              </a>
                            ) : null}
                          </span>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {isAdvisor ? null : (
            <Privacy>
              {t(
                'cockpit_interest_privacy',
                'Kontaktdaten von Interessenten werden aus Datenschutzgründen nur Ihrem Berater angezeigt.',
              )}
            </Privacy>
          )}
        </section>

        <aside className="side">
          {reasons.length ? (
            <section
              className="card"
              data-widget="cockpitRejectionReasonsGraphWidget"
            >
              <div className="sec">
                <h2>{t('cockpit_interest_reasons', 'Absagegründe')}</h2>
              </div>
              <Bars
                rows={reasons.map((r) => ({
                  label: r.reason,
                  value: r.count,
                }))}
              />
            </section>
          ) : null}
          {internalName || externalName ? (
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_interest_agents', 'Vermarktung')}</h2>
              </div>
              <dl className="dl">
                {internalName ? (
                  <div>
                    <dt>{t('cockpit_interest_agent_int', 'Intern')}</dt>
                    <dd>{internalName}</dd>
                  </div>
                ) : null}
                {externalName ? (
                  <div>
                    <dt>{t('cockpit_interest_agent_ext', 'Extern')}</dt>
                    <dd>{externalName}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}
        </aside>
      </div>
    </V3Page>
  );
}
